// src/hooks/useLearnerAnalytics.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { learnerAnalyticsService } from '../services/learnerAnalyticsService';

/**
 * Hook for tracking and retrieving learner analytics
 */
export const useLearnerAnalytics = (bookId, taskId, userId) => {
  const [analytics, setAnalytics] = useState(null);
  const [trend, setTrend] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Session tracking refs
  const activeSessionRef = useRef(null);
  const sessionAnswersRef = useRef([]);

  // Load analytics data
  const loadAnalytics = useCallback(async () => {
    if (!bookId || !taskId || !userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [analyticsData, trendData, sessionsData, insightsData] = await Promise.all([
        learnerAnalyticsService.getLearnerAnalytics(bookId, taskId, userId),
        learnerAnalyticsService.getPerformanceTrend(bookId, taskId, userId, 30),
        learnerAnalyticsService.getSessionHistory(bookId, taskId, userId, 10),
        learnerAnalyticsService.getImprovementInsights(bookId, taskId, userId)
      ]);

      setAnalytics(analyticsData);
      setTrend(trendData);
      setSessions(sessionsData);
      setInsights(insightsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bookId, taskId, userId]);

  // Load comparison data (separate call as it's heavier)
  const loadComparison = useCallback(async () => {
    if (!bookId || !taskId || !userId) return;

    try {
      const comparisonData = await learnerAnalyticsService.getLearnerComparison(bookId, taskId, userId);
      setComparison(comparisonData);
    } catch (err) {
      console.error('Error loading comparison:', err);
    }
  }, [bookId, taskId, userId]);

  // Start a study session
  const startSession = useCallback(async () => {
    if (!bookId || !taskId || !userId) return null;

    try {
      const session = await learnerAnalyticsService.startSession(bookId, taskId, userId);
      activeSessionRef.current = session.id;
      sessionAnswersRef.current = [];
      return session;
    } catch (err) {
      console.error('Error starting session:', err);
      return null;
    }
  }, [bookId, taskId, userId]);

  // Record an answer
  const recordAnswer = useCallback(async (answerData) => {
    if (!bookId || !activeSessionRef.current) {
      // Store for later if no active session
      sessionAnswersRef.current.push(answerData);
      return;
    }

    try {
      await learnerAnalyticsService.recordAnswer(bookId, activeSessionRef.current, answerData);
      sessionAnswersRef.current.push(answerData);
    } catch (err) {
      console.error('Error recording answer:', err);
      // Still store locally
      sessionAnswersRef.current.push(answerData);
    }
  }, [bookId]);

  // Complete the session
  const completeSession = useCallback(async () => {
    if (!bookId || !userId) return null;

    try {
      let result;

      if (activeSessionRef.current) {
        // Complete existing session
        result = await learnerAnalyticsService.completeSession(bookId, activeSessionRef.current, userId);
      } else if (sessionAnswersRef.current.length > 0) {
        // Record as quick session
        result = await learnerAnalyticsService.recordQuickSession(bookId, taskId, userId, {
          answers: sessionAnswersRef.current,
          duration: sessionAnswersRef.current.reduce((sum, a) => sum + (a.timeSpent || 0), 0)
        });
      }

      // Reset refs
      activeSessionRef.current = null;
      sessionAnswersRef.current = [];

      // Refresh analytics
      await loadAnalytics();

      return result;
    } catch (err) {
      console.error('Error completing session:', err);
      return null;
    }
  }, [bookId, taskId, userId, loadAnalytics]);

  // Record a quick session (for backward compatibility)
  const recordQuickSession = useCallback(async (sessionData) => {
    if (!bookId || !taskId || !userId) return null;

    try {
      const result = await learnerAnalyticsService.recordQuickSession(bookId, taskId, userId, sessionData);
      await loadAnalytics();
      return result;
    } catch (err) {
      console.error('Error recording quick session:', err);
      return null;
    }
  }, [bookId, taskId, userId, loadAnalytics]);

  // Initial load
  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  return {
    // Data
    analytics,
    trend,
    sessions,
    comparison,
    insights,
    loading,
    error,

    // Actions
    loadAnalytics,
    loadComparison,
    startSession,
    recordAnswer,
    completeSession,
    recordQuickSession,

    // Computed values
    questionStats: analytics?.questionStats || null,
    progress: analytics?.progress || null,
    dailyStats: trend?.dailyStats || [],
    trendSummary: trend?.summary || null,
    isImproving: trend?.summary?.isImproving ?? null
  };
};

/**
 * Hook for task-level analytics (for owners/admins)
 */
export const useTaskAnalytics = (bookId, taskId) => {
  const [taskAnalytics, setTaskAnalytics] = useState(null);
  const [questionAnalytics, setQuestionAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTaskAnalytics = useCallback(async () => {
    if (!bookId || !taskId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [taskData, questionData] = await Promise.all([
        learnerAnalyticsService.getTaskAnalytics(bookId, taskId),
        learnerAnalyticsService.getQuestionAnalytics(bookId, taskId)
      ]);

      setTaskAnalytics(taskData);
      setQuestionAnalytics(questionData);
    } catch (err) {
      console.error('Error loading task analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bookId, taskId]);

  useEffect(() => {
    loadTaskAnalytics();
  }, [loadTaskAnalytics]);

  return {
    taskAnalytics,
    questionAnalytics,
    loading,
    error,
    refresh: loadTaskAnalytics,

    // Convenience accessors
    totalLearners: taskAnalytics?.totalLearners || 0,
    activeLearners: taskAnalytics?.activeLearners || 0,
    avgMastery: taskAnalytics?.avgMastery || 0,
    avgAccuracy: taskAnalytics?.avgAccuracy || 0,
    learnerBreakdown: taskAnalytics?.learnerBreakdown || {},
    learners: taskAnalytics?.learners || []
  };
};

export default useLearnerAnalytics;